'use client';
import { jsx, jsxs } from 'react/jsx-runtime';
import classNames from 'classnames';
import { createContext, forwardRef, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import FocusLockCombination from '../../node_modules/react-focus-lock/dist/es2015/Combination.js';
import { getTestLabels } from '../../utils/getTestLabels.js';
import { PrimaryButton, SecondaryButton, InverseButton } from '../Button/Button.js';
import { IconButton } from '../Button/IconButton.js';
import style from './Modal.module.scss.js';

const { componentTestLabel, elementTestLabel } = getTestLabels('Modal');
const ModalContext = createContext({ isLoading: false });
const isClientSide = typeof window !== 'undefined' && typeof document !== 'undefined';
/**
 * [design (Figma)](https://www.figma.com/design/MUnGlxUUspXSbRkFsEXWGQ/%F0%9F%92%A0-Core-Components?node-id=6033-48499&node-type=section&t=L12XIxh9aK1lGzgC-0)
 * | [source code](https://src.private.zooplus.net/projects/FE/repos/ui-components/browse/src/components/Modal/Modal.tsx)
 *
 * @param {string} closeBtnAriaLabel - (required) An accessible label (in the current language) for the close button element. Use a phrase like "Close modal" or "Close this dialog" (in the current language).
 * @param {boolean} closeOnBackdropClick - (optional) Set to `false` if clicking the backdrop should **not** cause the modal to close. Defaults to `true`.
 * @param {React.ReactNode} heading - Text/markup to be used for the modal title.
 * @param {string} headingId - (required) A unique ID for the heading (make sure it's unique within the page/document).
 * @param {boolean} isLoading - (optional) For use when the modal is in a loading state.
 * @param {boolean} isOpen - (optional) Whether or not the modal is displayed.
 * @param {boolean} isShown - **DEPRECATED** (optional) Use `isOpen` instead.
 * @param {function} onClose - A custom function to call when the modal is closed.
 * @param {string} portalId - (Recommended!) If provided, the modal will be rendered in a React Portal. This ID must be unique!
 * @param {string} size - (optional) Controls the modal width. One of `sm`, `md`, or `lg` (default).
 */
const ModalWrapper = forwardRef((props, ref) => {
    const { children, className, closeBtnAriaLabel, closeOnBackdropClick = true, isLoading = false, isOpen = false, isShown = false, onClose, heading, headingId, portalId, size = 'lg', ...otherProps } = props;
    const [modalPortalElem, setModalPortalElem] = useState();
    const modalPortalId = `uic-modal-portal-root-${portalId}`;
    useEffect(() => {
        if (!portalId || !isClientSide)
            return;
        if (!document.getElementById(modalPortalId)) {
            const el = document.createElement('div');
            el.id = modalPortalId;
            document.body.appendChild(el);
        }
        setModalPortalElem(document.getElementById(modalPortalId) || undefined);
    }, [modalPortalId, portalId]);
    const isOpenOrShown = isOpen || isShown;
    const modalClasses = [style['z-modal']];
    const innerModal = useRef(null);
    // Save mouse down target to avoid closing when dragging the click or clicking the scroll bar
    let mouseDownTarget;
    if (isOpenOrShown) {
        modalClasses.push(style['z-modal--open']);
    }
    if (isLoading) {
        modalClasses.push(style['z-modal--loading']);
    }
    const handleOnClose = (evt) => {
        !isLoading && onClose(evt);
    };
    const handleKeyDown = (evt) => {
        if (evt.key === 'Escape') {
            handleOnClose(evt);
        }
    };
    const handleModalClick = (evt) => {
        if (closeOnBackdropClick &&
            evt.target instanceof HTMLElement &&
            mouseDownTarget === evt.target &&
            innerModal.current &&
            !innerModal.current.contains(evt.target)) {
            // only close if the click is on the backdrop
            handleOnClose(evt);
        }
    };
    const handleMouseDown = (evt) => {
        mouseDownTarget = evt.target;
    };
    const focusLockedModal = () => (
    // Interactions should be allowed for dialogs or alert components
    /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
    jsx(ModalContext.Provider, { value: { isLoading }, children: jsx(FocusLockCombination, { returnFocus: true, 
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus: true, disabled: !isOpenOrShown, className: style['z-modal-focus-lock'], children: jsx("div", { "data-zta": componentTestLabel, ref: ref, ...otherProps, onKeyDown: handleKeyDown, onClick: handleModalClick, onMouseDown: handleMouseDown, className: classNames(modalClasses), role: "dialog", "aria-modal": "true", "aria-labelledby": headingId, 
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex: 0, children: jsxs("div", { "data-zta": elementTestLabel('Main'), className: classNames(style['z-modal__box'], style[`z-modal__box--size-${size}`], className), ref: innerModal, children: [jsxs("div", { "data-zta": elementTestLabel('Header'), className: classNames(style['z-modal__header']), children: [jsx("h2", { "data-zta": elementTestLabel('Title'), id: headingId, className: style['z-modal__title'], children: heading }), jsx(IconButton, { variant: "ghost", size: "medium", "data-zta": elementTestLabel('CloseButton'), className: classNames(style['z-modal__close']), ariaLabel: closeBtnAriaLabel, onClick: handleOnClose, disabled: isLoading })] }), children] }) }) }) }));
    return portalId && isClientSide
        ? createPortal(focusLockedModal(), modalPortalElem || document.body)
        : focusLockedModal();
});
ModalWrapper.displayName = 'ModalWrapper';
const ModalBody = forwardRef((props, ref) => {
    const { children, className, ...otherProps } = props;
    return (jsx("div", { "data-zta": elementTestLabel('Body'), ref: ref, ...otherProps, 
        // fix for scrollable-region-focusable https://dequeuniversity.com/rules/axe/4.8/scrollable-region-focusable
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        // tabIndex={0}
        // removed because this can break click handlers of some items within the modal =/
        className: classNames(style['z-modal__content'], className), children: children }));
});
ModalBody.displayName = 'ModalBody';
const ModalFooter = forwardRef((props, ref) => {
    const { className, ...otherProps } = props;
    return (jsx("div", { "data-zta": elementTestLabel('Footer'), ref: ref, ...otherProps, className: classNames(style['z-modal__footer'], className) }));
});
ModalFooter.displayName = 'ModalFooter';
const ModalPrimaryButton = forwardRef((props, ref) => {
    const { className, children, ...otherProps } = props;
    return (jsx(ModalContext.Consumer, { children: ({ isLoading }) => (jsx(PrimaryButton, { "data-zta": elementTestLabel('PrimaryButton'), isLoading: isLoading, ref: ref, ...otherProps, className: classNames(style['z-modal__button'], className), children: children })) }));
});
ModalPrimaryButton.displayName = 'ModalPrimaryButton';
const ModalSecondaryButton = forwardRef((props, ref) => {
    const { className, children, ...otherProps } = props;
    return (jsx(ModalContext.Consumer, { children: ({ isLoading }) => (jsx(SecondaryButton, { "data-zta": elementTestLabel('SecondaryButton'), ref: ref, ...otherProps, className: classNames(style['z-modal__button'], className), disabled: isLoading, children: children })) }));
});
ModalSecondaryButton.displayName = 'ModalSecondaryButton';
const ModalNegativeButton = forwardRef((props, ref) => {
    const { className, children, ...otherProps } = props;
    return (jsx(ModalContext.Consumer, { children: ({ isLoading }) => (jsx(InverseButton, { "data-zta": elementTestLabel('NegativeButton'), ref: ref, ...otherProps, className: classNames(style['z-modal__button'], className), disabled: isLoading, children: children })) }));
});
ModalNegativeButton.displayName = 'ModalNegativeButton';

export { ModalBody, ModalFooter, ModalNegativeButton, ModalPrimaryButton, ModalSecondaryButton, ModalWrapper };
//# sourceMappingURL=Modal.js.map
